/**
 * Exécution des commandes d'assistance reçues par le terminal.
 *
 * Chaque commande produit un « plan » : l'accusé à envoyer (ack ou failed + résultat)
 * et, pour les commandes qui ferment ou rechargent la page, l'action à lancer APRÈS
 * l'accusé. Sinon la page disparaît avant l'accusé et la commande reste « sent ».
 *
 * Les ponts natifs varient selon la version de l'APK installée :
 *   - VisionPOSRemoteOps : à partir de 1.5.9.16 ;
 *   - VisionPOSUpdater.triggerUpdate : toutes versions (checkForUpdate n'a jamais été
 *     exposé à la page, d'où les 15 force_update « updater unavailable » de juillet).
 */

export type DeviceCommandType =
  | 'force_update'
  | 'kill_task'
  | 'reboot'
  | 'clear_cache'
  | 'reload_config'
  | 'force_logout'
  | 'ping'
  | 'enable_kiosk'
  | 'disable_kiosk'
  | 'take_screenshot';

export type PendingCommand = {
  id: string;
  command_type: DeviceCommandType | string;
  payload: unknown;
  created_at: string;
  expires_at: string;
};

export type RemoteOpsBridge = {
  ping?: () => string;
  getCapabilities?: () => string;
  reloadConfig?: () => void;
  clearCache?: () => void;
  killTask?: () => void;
  reboot?: () => boolean;
  takeScreenshot?: () => string | null;
};

export type UpdaterBridge = {
  triggerUpdate?: () => void;
  getCurrentVersion?: () => string;
  getCurrentVersionCode?: () => number;
};

export type CommandEnv = {
  remote: RemoteOpsBridge | null;
  updater: UpdaterBridge | null;
  storage: Storage | null;
  userAgent: string;
  reloadPage: () => void;
  signOutLocal: () => Promise<void>;
  goToAuth: () => void;
};

export type CommandResult = Record<string, unknown>;

export type CommandPlan = {
  ok: boolean;
  result: CommandResult;
  /** Action lancée après l'accusé (fermeture, rechargement, mise à jour). */
  run?: () => void | Promise<void>;
};

/** Commandes qui ne coupent jamais une vente : livrées même caisse occupée. */
export const NON_DISRUPTIVE_COMMANDS: ReadonlySet<string> = new Set(['ping', 'take_screenshot']);

/** Première version de l'APK qui s'installe sans écran Android (session PackageInstaller). */
export const SESSION_INSTALL_MIN_CODE = 70;

/**
 * Clés conservées par « vider le cache » : identité du terminal, session, ventes
 * hors ligne pas encore envoyées (données fiscales), état de la mise à jour.
 */
const PRESERVED_KEY_PATTERNS = [
  'visionpos.device.id',
  'visionpos-auth',
  'sb-',
  'posvision_offline_sales',
  'vp_waiter_offline',
  'posvision_cart',
  'vp_updater_last_event',
];

export function clearLocalCache(storage: Storage): number {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && !PRESERVED_KEY_PATTERNS.some((p) => key.includes(p))) keys.push(key);
  }
  keys.forEach((k) => storage.removeItem(k));
  return keys.length;
}

function parseJson(raw: string | null | undefined): CommandResult {
  if (!raw) return {};
  try {
    const value: unknown = JSON.parse(raw);
    return value && typeof value === 'object' ? (value as CommandResult) : {};
  } catch {
    return {};
  }
}

function planPing(env: CommandEnv): CommandPlan {
  if (env.remote?.ping) {
    return { ok: true, result: { source: 'native', ...parseJson(env.remote.ping()) } };
  }
  return { ok: true, result: { source: 'web', ts: Date.now(), ua: env.userAgent.slice(0, 160) } };
}

function planReboot(env: CommandEnv): CommandPlan {
  const remote = env.remote;
  if (!remote?.reboot)
    return { ok: false, result: { reason: 'pont natif absent (APK < 1.5.9.16 ou navigateur)' } };
  const caps = parseJson(remote.getCapabilities?.());
  // D3, D3 PRO, D3 MINI, V2s : pas de « su ». Sans root, Android interdit le redémarrage.
  if (caps.reboot !== true)
    return {
      ok: false,
      result: { reason: 'redémarrage impossible : terminal sans accès root', capabilities: caps },
    };
  return { ok: true, result: { source: 'native' }, run: () => void remote.reboot?.() };
}

function planForceUpdate(env: CommandEnv): CommandPlan {
  const updater = env.updater;
  if (!updater?.triggerUpdate)
    return {
      ok: false,
      result: { reason: 'mise à jour APK impossible : navigateur, pas l’application' },
    };
  const code = Number(updater.getCurrentVersionCode?.() ?? 0);
  const result: CommandResult = {
    source: 'native',
    current_version: updater.getCurrentVersion?.() ?? null,
    current_code: code || null,
    // Avant 1.5.9.33 : écran Android « Installer » à valider sur place.
    install_mode: code >= SESSION_INSTALL_MIN_CODE ? 'session' : 'ecran_android',
  };
  return { ok: true, result, run: () => updater.triggerUpdate?.() };
}

function planKill(env: CommandEnv): CommandPlan {
  const remote = env.remote;
  if (!remote?.killTask)
    return { ok: false, result: { reason: 'pont natif absent (APK < 1.5.9.16 ou navigateur)' } };
  // L'application se ferme sans se rouvrir : il faudra la relancer par son icône.
  return {
    ok: true,
    result: { source: 'native', reopen: 'manuel' },
    run: () => remote.killTask?.(),
  };
}

function planReload(env: CommandEnv): CommandPlan {
  const remote = env.remote;
  if (remote?.reloadConfig)
    return { ok: true, result: { source: 'native' }, run: () => remote.reloadConfig?.() };
  return { ok: true, result: { source: 'web' }, run: env.reloadPage };
}

function planClearCache(env: CommandEnv): CommandPlan {
  const removed = env.storage ? clearLocalCache(env.storage) : 0;
  const remote = env.remote;
  return {
    ok: true,
    result: { removed_keys: removed, kept: 'session, ventes hors ligne, panier' },
    run: () => {
      remote?.clearCache?.();
      env.reloadPage();
    },
  };
}

function planKiosk(env: CommandEnv, enable: boolean): CommandPlan {
  try {
    env.storage?.setItem('vp_kiosk_mode', enable ? 'true' : 'false');
  } catch {
    /* stockage indisponible : le rechargement garde l'ancien mode */
  }
  const reload = planReload(env);
  return { ...reload, result: { ...reload.result, kiosk: enable } };
}

function planScreenshot(env: CommandEnv): CommandPlan {
  const b64 = env.remote?.takeScreenshot?.();
  // L'image n'est ni stockée ni transmise (écran de caisse = données clients).
  if (b64) return { ok: true, result: { format: 'png_base64', size: b64.length, stored: false } };
  return { ok: false, result: { reason: 'capture indisponible' } };
}

export function planCommand(cmd: PendingCommand, env: CommandEnv): CommandPlan {
  try {
    switch (cmd.command_type) {
      case 'ping':
        return planPing(env);
      case 'reload_config':
        return planReload(env);
      case 'clear_cache':
        return planClearCache(env);
      case 'force_logout':
        return {
          ok: true,
          result: { logged_out: true },
          run: async () => {
            await env.signOutLocal();
            env.goToAuth();
          },
        };
      case 'kill_task':
        return planKill(env);
      case 'reboot':
        return planReboot(env);
      case 'force_update':
        return planForceUpdate(env);
      case 'take_screenshot':
        return planScreenshot(env);
      case 'enable_kiosk':
      case 'disable_kiosk':
        return planKiosk(env, cmd.command_type === 'enable_kiosk');
      default:
        return { ok: false, result: { reason: 'commande inconnue', received: cmd.command_type } };
    }
  } catch (e) {
    return { ok: false, result: { error: e instanceof Error ? e.message : String(e) } };
  }
}
